import { useEffect, ReactNode } from 'react';
import { useAuth } from '@/lib/auth/AuthContext';
import { useLocation } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import { publicPaths } from '@/lib/auth/config';

interface AuthGuardProps {
  children: ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading, signIn } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Don't redirect if we're still loading auth status
    if (isLoading) return;

    // Don't redirect if we're on a public path
    if (publicPaths.includes(location.pathname)) return;

    // Automatically redirect to Cognito login if not authenticated
    if (!isAuthenticated) {
      // Store the current path so we can redirect back after login
      sessionStorage.setItem('redirectAfterLogin', location.pathname);
      signIn();
    }
  }, [isAuthenticated, isLoading, location.pathname, signIn]);

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <LoadingSpinner size="lg" message="Checking authentication..." />
      </div>
    );
  }

  // Show loading while redirecting to Cognito login
  if (!isAuthenticated && !publicPaths.includes(location.pathname)) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <LoadingSpinner size="lg" message="Redirecting to sign in..." />
      </div>
    );
  }

  // Render children if authenticated or on public path
  return <>{children}</>;
}